import { useMemo, useState } from "react";
import { PageHead } from "@/components/PageHead";

import { Chip } from "@/components/Chip";
import { Donut } from "@/components/Donut";
import { CollapsibleSection } from "@/components/Section";
import { useScope } from "@/modules/connect/scope";
import { SIMS, SIM_BATCHES, type Sim, type SimBatch } from "@/modules/connect/mock";
import { SoonButton } from "@/components/SoonButton";

import { ActionRow } from "@/components/ActionRow";
import { Icon } from "@/components/icons";
import { download } from "@/lib/download";
/* ============================ ONBOARDING ============================ */
export function ConnectOnboardingPage() {
  const [batches, setBatches] = useState<SimBatch[]>(SIM_BATCHES);
  const active = batches.filter((b) => !b.archived);
  const archived = batches.filter((b) => b.archived);
  const set = (id: string, patch: Partial<SimBatch>) =>
    setBatches((bs) => bs.map((b) => (b.id === id ? { ...b, ...patch } : b)));

  const sample = () => {
    const csv =
      "iccid,imsi,msisdn_13,provider,plan_code,tenant,whitelist_ip,notes\n" +
      "8991881200426112090,404450426112090,5970421883421,airtel,A50P,SwiftFleet,10.244.1.17/32,example row\n" +
      "8991100233008812745,405860088127450,5972722881274,jio,J30,SwiftFleet,10.245.0.44/32,\n";
    download("orbit_activation_sample.csv", csv);
  };

  return (
    <div className="mpage on">
      <PageHead eyebrow="Connect · Onboarding" title="Import, validate, activate"
        right={<button className="btn ghost" onClick={sample}>⭳ Download sample sheet</button>} />

      <div className="grid2">
        <div className="dropzone" role="button" tabIndex={0}>
          <b>Drop activation sheet here (.csv / .xlsx)</b>
          Columns: ICCID · IMSI · MSISDN-13 · provider · plan_code · tenant ·{" "}
          <b style={{ display: "inline", color: "var(--t-body)" }}>whitelist_ip</b> · notes
          <div style={{ marginTop: 10 }}>
            <Chip tone="info">VALIDATED BEFORE SUBMISSION</Chip>
          </div>
        </div>
        <div className="panel panel-pad" style={{ paddingTop: 12, paddingBottom: 12 }}>
          <div className="dlab" style={{ marginBottom: 6 }}>
            Validation - activation_batch_jul09.csv · 500 rows
          </div>
          {[
            ["okv", "496 rows valid - ICCID checksum, 13-digit MSISDN, plan codes exist"],
            ["okv", "CMP reconciliation - all 496 ICCIDs found in Airtel IoT Hub inventory"],
            ["okv", "Whitelist IPs valid IPv4/CIDR · pushed to private-APN firewall config"],
            ["badv", "Row 214, 380 - duplicate ICCID (already active on SwiftFleet)"],
            ["badv", "Row 097 - ICCID checksum invalid (typo?)"],
            ["badv", 'Row 455 - whitelist_ip "10.244.9.999/32" not a valid address'],
          ].map(([cls, text], i) => (
            <div className={`vstep ${cls}`} key={i}>
              <span className="vic"><Icon name={cls === "okv" ? "ok" : "close"} size={13} /></span> {text}
            </div>
          ))}
          <ActionRow top={10}>
            <SoonButton className="btn teal" why="enrols the validated rows">Submit 496 valid rows</SoonButton>
            <SoonButton className="btn ghost" why="downloads the rejected rows with reasons">⭳ Error report (4 rows)</SoonButton>
          </ActionRow>
        </div>
      </div>

      <CollapsibleSection title="Previous batches" count={active.length}>
        {active.map((b) => (
          <SimBatchCard key={b.id} b={b} onArchive={() => set(b.id, { archived: true })} />
        ))}
      </CollapsibleSection>
      <CollapsibleSection title="Archived batches - kept for audit" count={archived.length}>
        {archived.map((b) => (
          <SimBatchCard key={b.id} b={b} onUnarchive={() => set(b.id, { archived: false })} />
        ))}
      </CollapsibleSection>
    </div>
  );
}

function SimBatchCard({
  b,
  onArchive,
  onUnarchive,
}: {
  b: SimBatch;
  onArchive?: () => void;
  onUnarchive?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const total = b.activated + b.approved + b.submitted + b.blocked + b.inventory;
  return (
    <div className="panel panel-pad" style={{ marginBottom: 12, opacity: b.archived ? 0.72 : 1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
        <b style={{ color: "var(--t-bright)", fontSize: 13 }}>{b.id}</b>
        <span style={{ fontSize: 11.5, color: "var(--t-mute)" }}>
          {b.meta} · {total} SIMs
        </span>
        <span style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <button className="btn ghost" onClick={() => setOpen((o) => !o)}>
            {open ? "▾" : "▸"} SIM list
          </button>
          <Chip tone={b.archived ? "mute" : b.state === "DONE" ? "mute" : "ok"}>
            {b.archived ? `ARCHIVED · ${b.state}` : b.state}
          </Chip>
          {b.archived ? (
            <button className="btn ghost" onClick={onUnarchive}>
              <Icon name="restore" /> Unarchive
            </button>
          ) : (
            b.state === "DONE" && (
              <button className="btn red" onClick={onArchive}>
                <Icon name="archive" /> Archive
              </button>
            )
          )}
        </span>
      </div>
      <Donut
        slices={[
          { label: "Activated · billing", value: b.activated, color: "var(--ok)" },
          { label: "KYC approved · queued", value: b.approved, color: "var(--info)" },
          { label: "KYC submitted · awaiting telco", value: b.submitted, color: "var(--warn)" },
          { label: "Blocked · awaiting fitment data", value: b.blocked, color: "var(--bad)" },
          { label: "Inventory · not paired", value: b.inventory, color: "var(--line2)" },
        ]}
      />
      {open && (
        <div style={{ marginTop: 10, fontSize: 11.5, color: "var(--t-dim)" }}>
          Per-SIM list - wired when the Connect backend exists.
        </div>
      )}
    </div>
  );
}

/* ============================ INVENTORY ============================ */
const STATE_CHIP: Record<Sim["state"], { tone: "ok" | "bad" | "warn" | "mute"; label: string }> = {
  active: { tone: "ok", label: "ACTIVE" },
  idle: { tone: "bad", label: "IDLE-ACTIVE" },
  kyc: { tone: "warn", label: "KYC PENDING" },
  inventory: { tone: "mute", label: "STOCK" },
  suspended: { tone: "mute", label: "SUSPENDED" },
};
const PROV_CLS = { airtel: "p-airtel", jio: "p-jio", vi: "p-vi" } as const;

export function ConnectInventoryPage() {
  const { ids, label } = useScope();
  const [filter, setFilter] = useState<"all" | Sim["state"]>("all");
  const [q, setQ] = useState("");
  const [drawer, setDrawer] = useState<Sim | null>(null);

  const rows = useMemo(
    () =>
      SIMS.filter((s) => {
        if (!ids.includes(s.co)) return false;
        if (filter !== "all" && s.state !== filter) return false;
        if (q && !`${s.iccid} ${s.dev} ${s.machine} ${s.prov} ${s.loc}`.toLowerCase().includes(q.toLowerCase()))
          return false;
        return true;
      }),
    [ids, filter, q],
  );

  const exportCsv = () => {
    const head = "iccid,msisdn_13,provider,company,device,machine_id,state,last_data,location,kyc,plan,whitelist_ip";
    const body = rows
      .map((s) =>
        [s.iccid, s.msisdn, s.prov, s.co, s.dev, s.machine, s.state, s.last, s.loc, s.kyc, s.plan, s.ip]
          .map((x) => `"${String(x).replace(/"/g, '""')}"`)
          .join(","),
      )
      .join("\n");
    download("orbit_connect_export.csv", `${head}\n${body}`);
  };

  const FILTERS: ("all" | Sim["state"])[] = ["all", "active", "idle", "kyc", "inventory", "suspended"];

  return (
    <div className="mpage on">
      <PageHead eyebrow="Connect · Inventory" title={`SIM registry - ${label}`}
        right={<button className="btn teal" onClick={exportCsv}>⭳ Export filtered CSV</button>} />

      <div className="toolrow">
        {FILTERS.map((f) => (
          <button key={f} className={`fchip${filter === f ? " on" : ""}`} onClick={() => setFilter(f)}>
            {f === "all" ? "All states" : STATE_CHIP[f].label}
          </button>
        ))}
        <input
          className="search"
          style={{ marginLeft: "auto" }}
          placeholder="ICCID · device · vehicle · location…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <div className="tblwrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>ICCID</th>
              <th>Provider</th>
              <th>Device</th>
              <th>Company</th>
              <th>Last data</th>
              <th>Last known location</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.iccid} onClick={() => setDrawer(s)} style={{ cursor: "pointer" }}>
                <td className="mono">{s.iccid}</td>
                <td>
                  <span className={`ptag ${PROV_CLS[s.prov]}`}>{s.prov.toUpperCase()}</span>
                </td>
                <td className="dev">{s.dev}</td>
                <td>{s.coName}</td>
                <td>
                  {s.idleDays >= 14 ? (
                    <span className="idlebad num">{s.idleDays} d silent</span>
                  ) : (
                    s.last
                  )}
                </td>
                <td className="loc">{s.loc}</td>
                <td>
                  <Chip tone={STATE_CHIP[s.state].tone}>{STATE_CHIP[s.state].label}</Chip>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 10.5, color: "var(--t-dim)", padding: "10px 4px 0" }}>
        Location = last GPS fix from the paired device (Fleet data, joined by ICCID↔device binding).
        Export honours the active filters.
      </div>

      {drawer && <SimDrawer sim={drawer} onClose={() => setDrawer(null)} />}
    </div>
  );
}

function SimDrawer({ sim, onClose }: { sim: Sim; onClose: () => void }) {
  const [planOpen, setPlanOpen] = useState(false);
  return (
    <>
      <div className="ovl open" onClick={onClose} />
      <aside className="drawer open">
        <div className="dhead">
          <div>
            <div className="dtitle mono">{sim.iccid}</div>
            <div className="dsub">
              {sim.dev} · {sim.coName}
            </div>
          </div>
          <button className="dclose" onClick={onClose}>
            <Icon name="close" />
          </button>
        </div>
        <div className="dbody">
          <div className="dsec">
            <div className="dlab">DoT KYC record</div>
            <Chip tone={sim.kyc === "ok" ? "ok" : "warn"}>
              {sim.kyc === "ok" ? "✓ DoT KYC COMPLETE" : "⚠ KYC PENDING - disconnect risk"}
            </Chip>
            <div style={{ marginTop: 10 }}>
              <KV k="M2M number (13-digit)" v={sim.msisdn} mono />
              <KV k="Device IMEI" v={sim.imei} mono />
              <KV k="Machine ID" v={sim.machine} />
              <KV k="Custodian" v={sim.custodian} />
            </div>
          </div>
          <div className="dsec">
            <div className="dlab">Connectivity &amp; billing</div>
            <KV k="Provider / plan" v={sim.plan} />
            <KV k="Whitelist IP (private APN)" v={sim.ip} mono />
            <KV k="Usage" v={sim.usage} />
            <KV k="Last data" v={sim.last} />
            <KV k="Last known location" v={sim.loc} />
            <KV k="Payment" v={sim.pay} />
          </div>
        </div>
        <div className="dfoot">
          <SoonButton className="btn red" why="suspends this SIM with the provider">Suspend</SoonButton>
          <button className="btn ghost" onClick={() => setPlanOpen(true)}>
            Change plan
          </button>
          <SoonButton className="btn ghost" why="opens this SIM in the provider console">Open in CMP ↗</SoonButton>
          <span className="note">
            Every action is audit-logged (actor · tenant · timestamp) and executed via the provider's
            CMP API (OCN-07).
          </span>
        </div>
      </aside>
      {planOpen && <PlanModal sim={sim} onClose={() => setPlanOpen(false)} />}
    </>
  );
}

function PlanModal({ sim, onClose }: { sim: Sim; onClose: () => void }) {
  const PLANS: Record<string, [string, string][]> = {
    airtel: [["25 MB pooled", "₹19/mo"], ["50 MB pooled", "₹28/mo"], ["100 MB pooled", "₹41/mo"]],
    jio: [["30 MB", "₹21/mo"], ["60 MB", "₹33/mo"], ["150 MB", "₹52/mo"]],
    vi: [["25 MB", "₹22/mo"], ["50 MB", "₹30/mo"], ["1 GB shared pool", "₹9/SIM +pool"]],
  };
  const [done, setDone] = useState(false);
  return (
    <>
      <div className="ovl open" style={{ zIndex: 98 }} onClick={onClose} />
      <div
        style={{
          position: "fixed",
          zIndex: 99,
          left: "50%",
          top: "50%",
          transform: "translate(-50%,-50%)",
          width: "min(430px,94vw)",
          background: "var(--surface)",
          border: "1px solid var(--line2)",
          borderRadius: 16,
          boxShadow: "var(--shadow)",
        }}
      >
        <div style={{ padding: "18px 22px", borderBottom: "1px solid var(--line)" }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "var(--t-bright)" }}>Change plan</div>
          <div className="mono" style={{ fontSize: 11, color: "var(--t-dim)", marginTop: 3 }}>
            {sim.iccid} · {sim.dev}
          </div>
        </div>
        <div style={{ padding: "16px 22px" }}>
          <div className="dlab">Available plans - {sim.prov.toUpperCase()} catalog (via CMP API)</div>
          {PLANS[sim.prov].map(([name, price]) => (
            <label className="copt" key={name}>
              <input type="radio" name="plan" defaultChecked={sim.plan.includes(name)} /> {name}
              <span className="n">
                <b style={{ color: "var(--t-bright)" }}>{price}</b>
              </span>
            </label>
          ))}
          <div className="dlab" style={{ marginTop: 16 }}>
            Effective
          </div>
          <label className="copt">
            <input type="radio" name="eff" defaultChecked /> Next billing cycle
            <span className="n">no pro-rating surprises</span>
          </label>
          <label className="copt">
            <input type="radio" name="eff" /> Immediately
            <span className="n">telco may pro-rate / reset counters</span>
          </label>
          <div style={{ fontSize: 10, color: "var(--t-dim)", marginTop: 12 }}>
            Executed as a rate-plan reassignment on the provider CMP. Audited: actor · tenant · old →
            new plan · timestamp.
          </div>
        </div>
        <div
          style={{
            padding: "14px 22px",
            borderTop: "1px solid var(--line)",
            display: "flex",
            gap: 8,
            alignItems: "center",
          }}
        >
          <button
            className="btn teal"
            onClick={() => {
              setDone(true);
              setTimeout(onClose, 900);
            }}
          >
            Confirm change
          </button>
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          {done && (
            <span style={{ marginLeft: "auto" }}>
              <Chip tone="ok">QUEUED VIA CMP API ✓</Chip>
            </span>
          )}
        </div>
      </div>
    </>
  );
}

/* ============================ COSTS & PLANS ============================ */
export function ConnectCostsPage() {
  const { companies, label } = useScope();
  const spend = companies.reduce((a, c) => a + c.spend.airtel + c.spend.jio + c.spend.vi, 0);
  const activated = companies.reduce((a, c) => a + c.states.active + c.states.idle, 0);

  return (
    <div className="mpage on">
      <PageHead eyebrow="Connect · Costs & Plans" title={`Connectivity economics - ${label}`} />

      <div style={{ display: "flex", alignItems: "flex-end", gap: 26, flexWrap: "wrap", marginBottom: 6 }}>
        <div>
          <div className="hero-num">
            <span className="rupee">₹</span>
            <span className="num">{spend.toLocaleString("en-IN")}</span>
          </div>
          <div className="hero-sub">
            July to date · <Chip tone="ok">↓ 4% vs June</Chip> · avg ₹
            {activated ? (spend / activated).toFixed(1) : "—"}/SIM
          </div>
        </div>
      </div>

      <div className="section-label">
        Available plans - fetched from CMP account catalogs{" "}
        <Chip tone="info">API WHERE SUPPORTED · ELSE MANUAL CATALOG</Chip>
      </div>
      <div className="chartgrid">
        {[
          ["AIRTEL", "p-airtel", [["25 MB pooled", "₹19/mo"], ["50 MB pooled · CURRENT", "₹28/mo"], ["100 MB pooled", "₹41/mo"]]],
          ["JIO", "p-jio", [["30 MB · CURRENT", "₹21/mo"], ["60 MB", "₹33/mo"], ["150 MB", "₹52/mo"]]],
          ["Vi", "p-vi", [["25 MB · CURRENT", "₹22/mo"], ["50 MB", "₹30/mo"], ["1 GB pool", "₹9/SIM"]]],
        ].map(([name, cls, plans]) => (
          <div className="panel plancol" key={name as string}>
            <div className="ph">
              <span className={`ptag ${cls}`}>{name as string}</span> rate plans
              <SoonButton className="btn ghost" why="re-reads rate plans from the provider">
                ↻ Fetch
              </SoonButton>
            </div>
            {(plans as [string, string][]).map(([p, price]) => (
              <div className="planitem" key={p}>
                <span>{p}</span>
                <b>{price}</b>
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="section-label">Payments</div>
      <div className="chartgrid">
        <div className="panel panel-pad">
          <div className="dlab" style={{ marginBottom: 8 }}>
            Upcoming payments
          </div>
          <div className="payrow">
            <span className="ptag p-jio">JIO</span>
            <span>
              July invoice <Chip tone="bad">OVERDUE 2 D</Chip>
            </span>
            <span className="amt num">₹6,300</span>
          </div>
          <div className="payrow">
            <span className="ptag p-airtel">AIRTEL</span>
            <span>July invoice · due 20 Jul</span>
            <span className="amt num">₹18,420</span>
          </div>
        </div>
        <div className="panel panel-pad">
          <div className="dlab" style={{ marginBottom: 8 }}>
            Last 3 payments - per operator
          </div>
          <div className="payrow">
            <span className="ptag p-airtel">AIRTEL</span>
            <span>
              Jun invoice · paid 18 Jun <Chip tone="ok">PAID</Chip>
            </span>
            <span className="amt num">₹19,940</span>
          </div>
          <div className="payrow">
            <span className="ptag p-jio">JIO</span>
            <span>
              Jun invoice · paid 12 Jun <Chip tone="ok">PAID</Chip>
            </span>
            <span className="amt num">₹6,300</span>
          </div>
        </div>
      </div>

      <div className="section-label">Money leaks - active SIMs sending nothing</div>
      <div className="panel panel-pad" style={{ paddingTop: 6, paddingBottom: 6 }}>
        <div className="rec">
          <span className="what">
            <b>37 idle-active SIMs</b> - billing, zero data ≥ 14 days
          </span>
          <span className="lose num">₹9,840/mo</span>
          <SoonButton className="btn teal" why="opens the flagged SIMs for review">Review list</SoonButton>
        </div>
        <div className="rec">
          <span className="what">
            <b>9 of them silent ≥ 30 days</b> - likely dead devices
          </span>
          <span className="lose num">₹3,120/mo</span>
          <SoonButton className="btn ghost" why="suspends every flagged SIM">Bulk suspend…</SoonButton>
        </div>
      </div>

      <div className="section-label">Plan vs average usage - per operator</div>
      <div className="panel panel-pad" style={{ paddingTop: 8, paddingBottom: 8 }}>
        <Dual op="AIRTEL" cls="p-airtel" plan="50 MB" sims={818} avg={9.4} pct={19} cap="81% of paid capacity never used → downgrade saves ₹4,880/mo" />
        <Dual op="JIO" cls="p-jio" plan="30 MB" sims={300} avg={27.8} pct={93} hot cap="Running hot - monthly throttle risk → upgrade the top 44" />
        <Dual op="Vi" cls="p-vi" plan="25 MB" sims={136} avg={11.2} pct={45} cap="Healthy fit - keep" />
      </div>
    </div>
  );
}

function Dual({
  op, cls, plan, sims, avg, pct, hot, cap,
}: {
  op: string; cls: string; plan: string; sims: number; avg: number; pct: number; hot?: boolean; cap: string;
}) {
  return (
    <div className="dual">
      <div className="dt">
        <span>
          <span className={`ptag ${cls}`}>{op}</span> {plan} plan · {sims} SIMs
        </span>
        <b className="num">
          avg {avg} MB · {pct}%
        </b>
      </div>
      <div className="lane">
        <div className="plan" />
        <div className={`used${hot ? " hot" : ""}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="cap">{cap}</div>
    </div>
  );
}

/* ---------- helpers ---------- */
function KV({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="kv">
      <span>{k}</span>
      <b className={mono ? "mono" : undefined}>{v}</b>
    </div>
  );
}

